import {
  Box,
  Button,
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@material-ui/core'
import { DragIndicator, RemoveCircle } from '@material-ui/icons'
import { FlexHorizontal, IBoard, MessageCard, Spacer } from '@vestaboard/installables'
import React, { useCallback, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { DraggableTableRow } from '../../../ui/DraggableTableRow'
import { DroppableTableBody } from '../../../ui/DropableTableBody'
import { MessageCreator } from './MessageCreator'

interface IIntroOutro {
  title: string
  messages: number[][][]
  setMessages: (messages: number[][][]) => void
  isNote?: boolean
}

export const IntroOutro = (props: IIntroOutro) => {
  const [isAddingIntro, setIsAddingIntro] = useState(false)
  const [editingIntro, setEditingIntro] = useState<{
    index: number
    value: IBoard
  } | null>(null)

  const onDragEnd = useCallback(
    (result: import('react-beautiful-dnd').DropResult) => {
      if (!result.destination) {
        return
      }

      const startIndex = result.source.index
      const endIndex = result.destination.index

      const newIntro = Array.from(props.messages)
      const [removed] = newIntro.splice(startIndex, 1)
      newIntro.splice(endIndex, 0, removed)
      props.setMessages(newIntro)
    },
    [props]
  )

  return (
    <>
      <FlexHorizontal spaceBetween>
        <Typography variant='h5'>{props.title}</Typography>
        <Button variant='outlined' onClick={() => setIsAddingIntro(true)}>
          Add {props.title} Message
        </Button>
      </FlexHorizontal>
      <Spacer size='large' />
      {props.messages.length ? (
        <TableContainer component={Paper}>
          <Table>
            <TableBody component={DroppableTableBody(onDragEnd)}>
              {props.messages.map((message, index) => (
                <TableRow key={index} component={DraggableTableRow(JSON.stringify(message), index)}>
                  <TableCell style={{ width: 80 }}>
                    <DragIndicator />
                  </TableCell>
                  <TableCell>
                    <ButtonBase
                      onClick={() => {
                        setEditingIntro({
                          index,
                          value: message as IBoard
                        })
                      }}>
                      <Box width={250}>
                        <MessageCard
                          characters={message as IBoard}
                          boardStyle={props?.isNote ? 'note-black' : 'black'}
                        />
                      </Box>
                    </ButtonBase>
                  </TableCell>
                  <TableCell style={{ width: 70 }}>
                    <FlexHorizontal>
                      <ButtonBase
                        onClick={() => {
                          const newIntro = [...props.messages]
                          newIntro.splice(index, 1)
                          props.setMessages(newIntro)
                        }}>
                        <RemoveCircle color='action' />
                      </ButtonBase>
                    </FlexHorizontal>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity='info'>There are no {props.title} messages added to this Q&A set yet.</Alert>
      )}
      <MessageCreator
        key={props.isNote ? 'note-intro-creator' : 'standard-intro-creator'}
        isNote={props.isNote}
        visible={isAddingIntro}
        setVisible={setIsAddingIntro}
        onSave={message => {
          props.setMessages([...props.messages, message])
        }}
      />
      {editingIntro ? (
        <MessageCreator
          key={props.isNote ? 'note-intro-creator' : 'standard-intro-creator'}
          isNote={props.isNote}
          visible={!!editingIntro}
          setVisible={() => setEditingIntro(null)}
          defaultValue={editingIntro?.value}
          onSave={message => {
            const newIntro = [...props.messages]
            newIntro[editingIntro!.index] = message
            props.setMessages(newIntro)
            setEditingIntro(null)
          }}
        />
      ) : null}
    </>
  )
}
